import { RxStatsDto } from "../../../../api-client";
import { useEffect, useState, useMemo } from "react";
import useStore from "../../../../helpers/useStore";
import { BIContext } from "../../../../stores/BIDashboard/biDashboard.provider";
import { calculateTotalRxValue } from "../../helpers/rxStatsHelpers";
import { useDebounce } from "../../../Transactions/Insurance/Base/hooks/useDebounce";

interface TotalRxValueData {
  locationId: string, 
  locationName: string,
  date: Date, 
  totalRxValue: number,
}

const useRxBlankoValues = (binType: "day" | "week" | "month" | "year", startDate: Date, endDate: Date, locationIds?: string[]) => {
  const BIStore = useStore(BIContext);
  const { getRxBlankoStats } = BIStore;

  // Debounce date changes
  const debounceStartDate = useDebounce(startDate, 300);
  const debounceEndDate = useDebounce(endDate, 300);

  // Memoize locationIds to avoid unnecessary re-renders
  const memoizedLocationIds = useMemo(() => {
    if (!locationIds || locationIds.length === 0) {
      return undefined;
    }
    return locationIds;
  }, [locationIds?.join(','), locationIds?.length]);

  // Store all fetched stats (for "all locations")
  const [allRxBlankoStats, setAllRxBlankoStats] = useState<RxStatsDto[]>([]);
  // Store filtered stats per selected locations
  const [rxBlankoStats, setRxBlankoStats] = useState<RxStatsDto[]>([]);
  const [totalRxBlankoValueResults, setTotalRxBlankoValueResults] = useState<TotalRxValueData[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        let response: RxStatsDto[] | undefined;

        if (debounceStartDate && debounceEndDate) {
          // console.log(`[Rx Blanko Value] Fetching data for date range: ${debounceStartDate} - ${debounceEndDate}`);
          // Always fetch all locations data, don't pass location filter to backend
          response = await getRxBlankoStats(debounceStartDate, debounceEndDate);
          // console.log("[Rx Blanko Value] Backend responded with:", response);
        } else {
          // console.log("[Rx Blanko Value] Fetching all data");
        }

        if (response && response.length > 0) {
          setAllRxBlankoStats(response);
          
          // Apply location filter immediately when setting data
          if (memoizedLocationIds && memoizedLocationIds.length > 0) {
            setRxBlankoStats(response.filter(p => memoizedLocationIds.includes(p.locationId)));
          } else {
            setRxBlankoStats(response);
          }
        } else {
          // console.log("[Rx Blanko Value] No data found for the specified range.");
          setAllRxBlankoStats([]);
          setRxBlankoStats([]);
        }
      } catch (error) {
        // console.log("[Rx Blanko Value] Error fetching Blanko Rx values:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [debounceStartDate, debounceEndDate, getRxBlankoStats]);

  // Filter locally whenever locations change (but not when data is initially loaded)
  useEffect(() => {
    if (!isLoading && allRxBlankoStats.length > 0) {
      if (memoizedLocationIds && memoizedLocationIds.length > 0) {
        setRxBlankoStats(allRxBlankoStats.filter(p => memoizedLocationIds.includes(p.locationId)));
      } else {
        // No location selected → show all
        setRxBlankoStats(allRxBlankoStats);
      }
    }
  }, [memoizedLocationIds, allRxBlankoStats, isLoading]);

  useEffect(() => {
    if (rxBlankoStats.length > 0) {
      const totalRxBlankoValues = calculateTotalRxValue(rxBlankoStats, binType);
      setTotalRxBlankoValueResults(totalRxBlankoValues);
    } else {
      setTotalRxBlankoValueResults([]);
    }
  }, [binType, rxBlankoStats]);

  const availableLocations = useMemo(() => {
    const locMap = new Map<string, string>();
    allRxBlankoStats.forEach((p) => {
      if (p.locationId) locMap.set(p.locationId, p.locationName || p.locationId);
    });
    return Array.from(locMap.entries()).map(([id, name]) => ({ id, name }));
  }, [allRxBlankoStats]);

  return { totalRxBlankoValueResults, isLoading, availableLocations };
};

export default useRxBlankoValues;